/**
 * Notification stream hook — opens an SSE connection to
 * `/communicator/notifications/stream` (via `event-source-polyfill` so
 * the bearer token rides as an `Authorization` header instead of being
 * stuffed into the URL), keeps local notification state in sync, and
 * exposes reconnect controls.
 *
 * Reconnect ownership: the polyfill auto-reconnects on error by default.
 * We close the instance in the `error` handler BEFORE scheduling our
 * own backoff retry so the two loops don't fight (which would create
 * duplicate `notification` frames and defeat the 5-attempt cap).
 *
 * Badge truth: the in-tray `unreadCount` is seeded from the authoritative
 * `/inbox?get_count=true&group_by=subcategory` REST query (via the
 * `useInboxCountsByCategory` hook) so the bell doesn't show 0 to a user
 * with 12 unread until a fresh live frame arrives. Live arrivals bump
 * the count locally; mark-read / mark-all decrement and then invalidate
 * the counts query so the next render re-reads the server total.
 *
 * List hydrate: the newest `/inbox` page (BE default page size, all
 * subcategories) seeds the tray list over REST — via TanStack under
 * `inboxTrayQueryKey`, so it is cached, retried and invalidatable rather
 * than a bare axios call. The SSE stream then only has to deliver what
 * happens next. initial_flush frames from the stream still merge harmlessly
 * (same id-dedupe) wherever the BE keeps sending them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  EventSourcePolyfill,
  type Event as PolyfillEvent,
  type MessageEvent as PolyfillMessageEvent,
} from 'event-source-polyfill';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL } from '@/modules/shared/constants';
import { extractErrorMessage } from '@/modules/shared/errorHandling';
import { fetchInbox } from '@/modules/notifications/list/api';
import { useInboxCountsByCategory } from '@/modules/notifications/list/useInboxCounts';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { getAccessToken } from '@/utils/helper';

import { markManyRead, markRead } from './api';
import { isSafeNavigationUrl } from './isSafeNavigationUrl';
import {
  openStreamChannel,
  requestStreamLeadership,
  supportsCrossTabStream,
  type StreamBroadcast,
} from './crossTabStream';
import { en, th } from './locales';
import bellIcon from '@/images/logo.svg';

export interface NotificationLike {
  id: number | string;
  title: string;
  content: string;
  image?: string | null;
  target_url?: string | null;
  is_unread: boolean;
  config_identifier?: string | null;
  priority?: number | null;
  // Admin-tab dimension (general/assignment/mention/…). Free string so ops
  // can add buckets without an FE redeploy. Null/missing → treat as general.
  subcategory?: string | null;
  // Denormalized tag list (VMI, CMI, …). Always normalised to alphanumeric+
  // dash/underscore on the backend.
  tags?: string[];
  // Free-form display bag — icon glyph, accent color, CTA copy, deep-link
  // params, etc. USE FOR DISPLAY + URL/CTA CONSTRUCTION ONLY. The backend
  // does NOT index or filter on these keys.
  extra_params?: Record<string, unknown>;
  created_at?: string | null;
  /** REST inbox shape carries the timestamp as `date` instead of `created_at`. */
  date?: string | null;
  source?: 'initial_flush' | 'live';
  silent?: boolean;
}

const MAX_TRAY_ITEMS = 100;

/** Cache key for the tray's hydrate slice (newest inbox page, all
 *  subcategories). Exported so callers can invalidate the tray list the same
 *  way `['notifications','inbox','counts']` invalidates the badge. */
export const inboxTrayQueryKey = ['notifications', 'inbox', 'tray'] as const;

/** Epoch millis for ordering; rows without a parseable timestamp sink last. */
const notificationEpoch = (n: NotificationLike): number => {
  const iso = n.created_at ?? n.date;
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Merge incoming rows into the tray list: dedupe by id (String-compared so a
 * REST numeric id and an SSE stringy id of the same row can't coexist),
 * newest-first by timestamp, capped. Shared by the SSE frame handler and the
 * REST hydrate so both sources obey identical semantics.
 */
const mergeNotifications = (prev: NotificationLike[], incoming: NotificationLike[]): NotificationLike[] => {
  const fresh = incoming.filter((n) => n && n.id !== undefined && !prev.some((p) => String(p.id) === String(n.id)));
  if (!fresh.length) return prev;
  return [...fresh, ...prev].sort((a, b) => notificationEpoch(b) - notificationEpoch(a)).slice(0, MAX_TRAY_ITEMS);
};
const HEARTBEAT_TIMEOUT_MS = 60_000;
const RECONNECT_BASE_MS = 5_000;
const RECONNECT_MAX_MS = 60_000;
const MAX_RECONNECT_ATTEMPTS = 5;
// Token transiently absent (mid token-refresh, etc.) — try again on this
// cadence instead of dead-ending until a manual reconnect.
const TOKEN_RETRY_MS = 5_000;

export interface UseNotificationStreamResult {
  connected: boolean;
  notifications: NotificationLike[];
  unreadCount: number;
  attempts: number;
  markRead: (id: number | string) => Promise<void>;
  markAllRead: () => Promise<void>;
  reconnect: () => void;
  navigate: (target_url: string | null | undefined) => void;
}

export interface UseNotificationStreamOptions {
  /**
   * Open the connection at all. False on the auth pages, where there is no token to open it
   * with — without this the hook would sit in its TOKEN_RETRY_MS loop behind the login form.
   *
   * It is an argument rather than a conditional mount because the only caller is
   * `NotificationStreamProvider`, which sits above the router: swapping it in and out would
   * remount the entire application under it.
   */
  enabled?: boolean;
}

export function useNotificationStream({
  enabled = true,
}: UseNotificationStreamOptions = {}): UseNotificationStreamResult {
  const { t } = useTranslation({ en, th });
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<NotificationLike[]>([]);
  // Local count for the bell — bumped by live frames, decremented by
  // mark-read. SEEDED from the REST count query below so the badge
  // doesn't render `0` to a user with standing unreads.
  const [unreadCount, setUnreadCount] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const esRef = useRef<EventSourcePolyfill | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attemptsRef = useRef(0);
  // Track whether the user has interacted with the tray yet. Until the
  // first interaction we never overwrite the local count with a stale
  // REST snapshot — but the initial render still benefits from the seed.
  const hasSeededRef = useRef(false);
  // False until the stream has connected once. A RE-connect means we were
  // offline for a window and may have missed `notification` frames; the first
  // connect needs no gap-fill (the hydrate query covers it).
  const hasConnectedRef = useRef(false);

  // Exactly one tab opens a connection; the rest read its frames off a BroadcastChannel.
  // See crossTabStream.ts. Starts false so a follower never opens one on the way to losing
  // the election; where the APIs are missing, leadership is granted synchronously and every
  // tab connects, which is the behaviour this replaces.
  const [isLeader, setIsLeader] = useState(!supportsCrossTabStream());
  // Read by openStream, which must not re-create itself when leadership changes: it is a
  // dependency of the effect that opens the connection, so a new identity would close and
  // reopen the very stream this file exists to keep alive.
  const isLeaderRef = useRef(isLeader);
  isLeaderRef.current = isLeader;
  const channelRef = useRef<BroadcastChannel | null>(null);

  const { data: countsData } = useInboxCountsByCategory({ is_unread: true, enabled });

  // Seed the badge from the authoritative REST total exactly once on
  // first arrival, and on every subsequent counts refresh that lands
  // before a live mutation. This is the single source of truth — local
  // mutations (mark-read/-all + live arrivals) drive it from there.
  useEffect(() => {
    if (countsData?.total === undefined) return;
    if (!hasSeededRef.current) {
      setUnreadCount(countsData.total);
      hasSeededRef.current = true;
    }
  }, [countsData?.total]);

  // Nudge the aggregated counts query whenever the tray's view of the
  // world changes (live arrival, mark-read, mark-all). The counts query
  // refetches against the backend so the admin-list tab badges + the
  // tray's seed source stay in lockstep.
  const queryClient = useQueryClient();
  const invalidateCounts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications', 'inbox', 'counts'] });
  }, [queryClient]);

  // Re-pull the tray's hydrate page. Used on stream RE-connect to fill the
  // gap left by frames that arrived while we were disconnected.
  const invalidateTray = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: inboxTrayQueryKey });
  }, [queryClient]);

  const closeStream = useCallback(() => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {
        // ignore
      }
      esRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const handleIncoming = useCallback(
    (payload: NotificationLike | null) => {
      if (!payload || typeof payload !== 'object' || payload.id === undefined) return;
      // Only live frames change the server-side tally. The initial_flush
      // burst is already reflected in the REST counts seed — invalidating
      // per flush frame fired N simultaneous /inbox?get_count calls on
      // every stream (re)connect.
      if (payload.source !== 'initial_flush') {
        invalidateCounts();
      }
      setNotifications((prev) => mergeNotifications(prev, [payload]));
      if (payload.is_unread !== false && payload.source !== 'initial_flush') {
        // initial-flush rows are already counted in the REST seed —
        // bumping for them would double-count.
        setUnreadCount((c) => c + 1);
      }
      // OS toast — gated by (a) permission granted, (b) not silent, (c) live
      // (initial-flush already-seen rows shouldn't pop a toast).
      if (
        payload.source !== 'initial_flush' &&
        !payload.silent &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'granted'
      ) {
        try {
          // eslint-disable-next-line no-new
          new Notification(payload.title || t('newNotificationFallbackTitle'), {
            body: payload.content || '',
            icon: payload.image || bellIcon,
          });
        } catch {
          // notifications blocked / browser quirks
        }
      }
    },
    [invalidateCounts, t],
  );

  // Lazy callable so the schedule path can reach `openStream` without a
  // forward-reference dance.
  const openStreamRef = useRef<() => void>(() => {});

  const scheduleReconnect = useCallback((delayMs: number) => {
    if (reconnectTimerRef.current) return;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      openStreamRef.current();
    }, delayMs);
  }, []);

  const openStream = useCallback(() => {
    // Guards the manual reconnect as well as the mount path: a follower must never open a
    // second connection behind the leader's back.
    if (!enabled || !isLeaderRef.current) return;
    const token = getAccessToken();
    if (!token) {
      setConnected(false);
      // Token transiently absent during a refresh — try again instead
      // of dead-ending until a manual reconnect. Does NOT count toward
      // the backoff cap because there's no real failure yet.
      scheduleReconnect(TOKEN_RETRY_MS);
      return;
    }
    closeStream();

    const url = `${API_BASE_URL}/communicator/notifications/stream`;
    const es = new EventSourcePolyfill(url, {
      headers: { Authorization: `Bearer ${token}` },
      heartbeatTimeout: HEARTBEAT_TIMEOUT_MS,
    });
    esRef.current = es;

    // `open` (polyfill) and `connected` (backend) both signal the same live
    // socket, so share one handler behind a per-instance latch — otherwise a
    // single connection would gap-fill twice.
    let announced = false;
    const onConnected = () => {
      // Same stale-instance guard the `notification`/`error` handlers use: a
      // closed socket's late event must not reset the backoff or trigger a
      // refetch against the current stream.
      if (esRef.current !== es) return;
      setConnected(true);
      channelRef.current?.postMessage({ type: 'connected' } satisfies StreamBroadcast);
      attemptsRef.current = 0;
      setAttempts(0);
      if (announced) return;
      announced = true;
      // Gap-fill on RE-connect only. Frames that landed while the stream was
      // down were never merged, so re-pull the newest inbox page (the merge
      // is id-deduped) and the counts. The counts query no longer refetches
      // on window focus, so this is the one place a missed frame gets
      // reconciled; letting the bell re-seed from the fresh total makes it
      // catch up too, instead of waiting for the next local mutation. The
      // first connect is skipped — both queries are already in flight.
      if (hasConnectedRef.current) {
        invalidateTray();
        hasSeededRef.current = false;
        invalidateCounts();
      }
      hasConnectedRef.current = true;
    };
    es.addEventListener('open', onConnected);
    es.addEventListener('connected', onConnected);
    es.addEventListener('notification', (event: PolyfillEvent) => {
      // Discard frames from a stale instance — guards against any
      // `error → close → reopen` race where the old socket fires a
      // last frame after we've already swapped the ref.
      if (esRef.current !== es) return;
      try {
        const payload = JSON.parse((event as PolyfillMessageEvent).data);
        handleIncoming(payload);
        // Relay to the other tabs, which hold no connection of their own.
        channelRef.current?.postMessage({ type: 'notification', payload } satisfies StreamBroadcast);
      } catch {
        // bad frame — ignore
      }
    });
    es.addEventListener('error', () => {
      // Ignore errors from a stale instance — own the lifecycle.
      if (esRef.current !== es) return;
      setConnected(false);
      channelRef.current?.postMessage({ type: 'disconnected' } satisfies StreamBroadcast);
      // CRITICAL: kill the polyfill instance BEFORE scheduling our
      // backoff retry. The polyfill auto-reconnects internally on
      // error; without closing first, the polyfill's loop runs in
      // parallel with ours → duplicate frames during flapping and
      // an infinite reconnect after we hit the 5-attempt cap.
      closeStream();
      if (attemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }
      const next = attemptsRef.current + 1;
      attemptsRef.current = next;
      setAttempts(next);
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (next - 1));
      scheduleReconnect(delay);
    });
  }, [enabled, closeStream, handleIncoming, scheduleReconnect, invalidateTray, invalidateCounts]);

  // Keep the scheduling ref aimed at the latest `openStream` so a
  // backoff timer fired across a closure refresh still calls the
  // current implementation.
  useEffect(() => {
    openStreamRef.current = openStream;
  }, [openStream]);

  // REST hydrate — pull the newest inbox page (`offset=0`, ALL subcategories
  // — no `limit`, the BE default page size decides; the tray still caps at
  // MAX_TRAY_ITEMS client-side) so the tray has history without depending on
  // the SSE initial_flush burst.
  //
  // Runs through TanStack (not raw axios) so it shares the client's 4xx-aware
  // retry, dedupes if the tray is ever mounted twice, and is invalidatable
  // under `inboxTrayQueryKey`. This slice is NOT the admin table's query —
  // that one is per-tab and paginated (`subcategory` + `limit`), so the two
  // are deliberately separate cache entries, not a duplicate fetch.
  //
  // Best-effort: on failure `data` stays undefined, the merge no-ops and the
  // stream's initial_flush covers us.
  const { data: trayPage } = useQuery({
    queryKey: inboxTrayQueryKey,
    queryFn: () => fetchInbox({ offset: 0 }),
    enabled,
    // The stream keeps this slice live once it is open, so a remount inside the session has
    // nothing to learn from refetching. Matches the counts query next door.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Rows merge through the same dedupe/sort/cap as stream frames; like
  // initial_flush they never bump the unread badge (the REST counts seed
  // already includes them) and never pop OS toasts. `mergeNotifications`
  // returns `prev` untouched when nothing is new, so re-running this against
  // a cached page is idempotent — no render loop, and an optimistic
  // `is_unread: false` on an existing row is never overwritten.
  useEffect(() => {
    const results = trayPage?.results;
    if (!results?.length) return;
    setNotifications((prev) => mergeNotifications(prev, results));
  }, [trayPage]);

  // Open the shared channel for as long as this tab is live. Both the leader (which posts)
  // and the followers (which read) need it.
  useEffect(() => {
    if (!enabled) return;
    const channel = openStreamChannel();
    channelRef.current = channel;
    return () => {
      channelRef.current = null;
      channel?.close();
    };
  }, [enabled]);

  // Stand for election. Exactly one tab wins; releasing on unmount hands the connection to
  // whichever tab is next in the browser's queue.
  useEffect(() => {
    if (!enabled) {
      setIsLeader(false);
      return;
    }
    const standDown = requestStreamLeadership(() => setIsLeader(true));
    return () => {
      standDown();
      setIsLeader(false);
    };
  }, [enabled]);

  // Only the leader holds a connection.
  useEffect(() => {
    if (!enabled || !isLeader) {
      closeStream();
      return;
    }
    openStream();
    return closeStream;
  }, [enabled, isLeader, openStream, closeStream]);

  // Followers apply the leader's frames through the same handler the leader uses, so the
  // dedupe, the badge and the toasts behave identically in every tab.
  useEffect(() => {
    if (!enabled || isLeader) return;
    const channel = channelRef.current;
    if (!channel) return;
    const onMessage = (event: MessageEvent<StreamBroadcast>) => {
      const message = event.data;
      if (!message) return;
      if (message.type === 'notification') {
        handleIncoming(message.payload as NotificationLike);
        return;
      }
      setConnected(message.type === 'connected');
    };
    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
  }, [enabled, isLeader, handleIncoming]);

  const markReadById = useCallback(
    async (id: number | string) => {
      // Optimistic update + REST. On failure we toast + invalidate the
      // counts so the next render re-syncs with the server, undoing the
      // optimistic decrement if it was wrong.
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_unread: false } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      try {
        await markRead(id);
        invalidateCounts();
      } catch (err) {
        toast.error(t('markReadFailed'), {
          description: extractErrorMessage(err as AxiosError | Error),
        });
        invalidateCounts();
      }
    },
    [invalidateCounts, t],
  );

  const markAllReadLocal = useCallback(async () => {
    const unreadIds = notifications.filter((n) => n.is_unread).map((n) => n.id);
    if (!unreadIds.length) return;
    const prevNotifications = notifications;
    const prevUnreadCount = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_unread: false })));
    setUnreadCount(0);
    try {
      await markManyRead(unreadIds);
      invalidateCounts();
    } catch (err) {
      // Restore so a server-side failure doesn't silently wipe rows
      // that streamed in mid-flight.
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
      toast.error(t('markAllReadFailed'), {
        description: extractErrorMessage(err as AxiosError | Error),
      });
      invalidateCounts();
    }
  }, [notifications, unreadCount, invalidateCounts, t]);

  const reconnect = useCallback(() => {
    attemptsRef.current = 0;
    setAttempts(0);
    openStream();
  }, [openStream]);

  const navigate = useCallback((target_url: string | null | undefined) => {
    if (!target_url) return;
    if (!isSafeNavigationUrl(target_url)) return;
    window.location.href = target_url;
  }, []);

  return {
    connected,
    notifications,
    unreadCount,
    attempts,
    markRead: markReadById,
    markAllRead: markAllReadLocal,
    reconnect,
    navigate,
  };
}
